import { useState, useEffect } from "react";
import ImageSlideShow from "../../ImageSlideShow";
import { useSliderState } from "../SliderState";
import TemplateUtils from "./utils/TemplateUtils";
import TemplateCTAs from "./TemplateCTAs";
import localization from "../../../../../assets/localization";
import InternalRating from "../../custom/HM/InternalRating";
import StarRating from "../../custom/HM/StarRating";

interface ImageItem {
  url: string;
  title: string;
}

interface Gallery {
  key: string;
  images: ImageItem[];
}

interface CTA {
  type: "slider_trigger" | "url";
  label: string;
  link?: string; // only for type = "url"
}

interface Offer {
  title: string;
  subtitle: string;
  placeholder_image: string;
}

interface Hotel {
  placeholder_image: string;
  title: string;
  stars: string;
  location: string;
  internal_rating: string; // consider making this `number` if numeric
  details: string[];
}

interface OfferTemplate {
  type: "hm_offer";
  offer: Offer;
  hotel: Hotel;
  galleries: Gallery[];
  ctas: CTA[];
}

const GALLERY_TEMP_INDEX: any = {
  hotel_gallery:
    "https://firebasestorage.googleapis.com/v0/b/plutonios-prod.appspot.com/o/HM%2Fhotel_icon.svg?alt=media&token=30ab2147-e00b-4bbc-b8e1-ff96a8a64575",
  rooms_gallery:
    "https://firebasestorage.googleapis.com/v0/b/plutonios-prod.appspot.com/o/HM%2Frooms_icon.svg?alt=media&token=7b541dbd-87fc-4336-9d0f-5743d0c50060",
  restaurant_gallery:
    "https://firebasestorage.googleapis.com/v0/b/plutonios-prod.appspot.com/o/HM%2Frestaurant_icon.svg?alt=media&token=a7f4fe7c-2591-45f0-bc92-fd73f264dda5",
  animation_gallery:
    "https://firebasestorage.googleapis.com/v0/b/plutonios-prod.appspot.com/o/HM%2Fanimation_icon.svg?alt=media&token=6ab34948-737f-4e5a-b47b-c9631d7233e5",
};

interface OfferSliderTemplateProps {
  template: OfferTemplate;
}

export default function HotelSliderTemplate({
  template,
}: OfferSliderTemplateProps) {
  const [imageIndex, setImageIndex] = useState(0);
  const [itemIndex, setItemIndex] = useState(0);
  const [roomTransitionFade, setRoomTransitionFade] = useState(true);

  const sliderState = useSliderState();

  useEffect(() => {
    setRoomTransitionFade(false); // Fade out
    const timeout = setTimeout(() => setRoomTransitionFade(true), 100); // Fade in after a short delay
    return () => clearTimeout(timeout);
  }, [itemIndex]);

  const findTemplateIndex = (templateTitle: string | null) => {
    setImageIndex(0);
    if (!templateTitle) return 0;

    if (!sliderState.templates || sliderState.templates.length === 0) return 0;

    const index = sliderState.templates.findIndex(
      (template: any) => template.title.toLowerCase() === templateTitle
    );

    return index !== -1 ? index : 0;
  };

  /* eslint-disable */
  useEffect(() => {
    const newIndex = findTemplateIndex(sliderState.selectedKeyword);
    if (newIndex !== itemIndex) {
      setItemIndex(newIndex);
    }
  }, [sliderState.selectedKeyword]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "inherit",
        borderTopLeftRadius: "inherit",
        borderTopRightRadius: "inherit",
        overflowY: "hidden",
      }}
    >
      <button
        className="hm-slider-minimize-button"
        onClick={sliderState.closeSlider}
      >
        <i className="fa-solid fa-x hm-slider-minimize-button-icon"></i>
      </button>

      <div
        style={{
          position: "relative",
          height: "100%",
          overflow: "hidden",
          borderTopLeftRadius: "inherit",
          borderTopRightRadius: "inherit",
        }}
      >
        {template &&
          template.galleries[sliderState.subTemplateItemIndex].images?.length >
            0 && (
            <ImageSlideShow
              shouldShowAvailable={false}
              images={
                template.galleries[sliderState.subTemplateItemIndex].images
              }
              title={
                TemplateUtils.capitalizeFirstLetter(
                  template?.galleries[sliderState.subTemplateItemIndex].images[
                    imageIndex
                  ].title
                ) ??
                localization.get(sliderState.language)[
                  template?.galleries[sliderState.subTemplateItemIndex].key
                ] ??
                ""
              }
              roomTransitionFade={roomTransitionFade}
              imageIndex={imageIndex}
              setImageIndex={setImageIndex}
            />
          )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div
          key={itemIndex}
          className="slider-room-description"
          style={{
            fontSize: "15px",
            margin: "1rem",
            marginBottom: "2rem",
            textAlign: "left",
            opacity: roomTransitionFade ? 1 : 0, // Ensure fade only applies when selectedRoom changes
            transition: "opacity 0.4s ease-in-out", // Smooth fade effect
          }}
        >
          <div className="hotel-template-title">
            {template.hotel.title}
            <StarRating stars={template.hotel.stars} />
          </div>
          <div className="hotel-template-subtitle">
            {template.offer.subtitle}
          </div>
          <div className="hotel-template-location">
            📍 {template.hotel.location}
          </div>
          {template.hotel.internal_rating && (
            <InternalRating rating={template.hotel.internal_rating} />
          )}
          <div className="hotel-template-galleries-title">
            {localization.get(sliderState.language)["look_hotel_galleries"]}
          </div>
          <div className="hotel-template-galleries">
            {template.galleries.map((gallery, index) => (
              <div key={index}>
                <button
                  className="hotel-template-gallery-button"
                  onClick={() => {
                    sliderState.setSubTemplateItemIndex(index);
                    setImageIndex(0);
                  }}
                >
                  <img
                    src={GALLERY_TEMP_INDEX[gallery.key]}
                    style={{ width: "50px" }}
                  />
                </button>
                <p className="hotel-template-gallery-button-label">
                  {localization.get(sliderState.language)[gallery.key]}
                </p>
              </div>
            ))}
          </div>
          <TemplateCTAs template={template} />
          <div style={{ marginTop: "2rem" }}>
            <button
              className="hotel-template-close-button"
              onClick={sliderState.closeSlider}
            >
              <i className="fa-x fa-solid"></i>
              {localization.get(sliderState.language)["back_to_chat"]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
